import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Clock, Video, ChevronRight } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Meeting } from '../../services/Calendar';
import { useI18n, type Dictionary } from '../../i18n';

interface MeetingCardProps {
  meeting: Meeting;
}

/**
 * Check if a meeting can be viewed (has summary content)
 * The detail page handles all summary states gracefully:
 * - 'processing': Shows "Processing..." message
 * - 'completed': Shows full summary with analysis
 * - 'failed': Shows error message
 */
const canViewMeetingDetails = (meeting: Meeting): boolean => {
  const isPastMeeting = new Date(meeting.startTime) < new Date();
  // Meeting is viewable if summary exists (any status)
  const hasSummary = meeting.summaryStatus !== null;
  return isPastMeeting && hasSummary;
};

const formatMeetingTime = (startTime: string, endTime: string, locale: string): string => {
  const start = new Date(startTime);
  const end = new Date(endTime);
  
  const timeOptions: Intl.DateTimeFormatOptions = {
    hour: 'numeric',
    minute: '2-digit',
    hour12: locale === 'en',
  };
  
  const dateOptions: Intl.DateTimeFormatOptions = {
    month: 'short',
    day: 'numeric',
  };
  
  const intlLocale = locale === 'sv' ? 'sv-SE' : 'en-US';
  const startTimeStr = start.toLocaleTimeString(intlLocale, timeOptions);
  const endTimeStr = end.toLocaleTimeString(intlLocale, timeOptions);
  const dateStr = start.toLocaleDateString(intlLocale, dateOptions);
  
  return `${dateStr} • ${startTimeStr} - ${endTimeStr}`;
};

const getBotStatusVariant = (status: string | null): 'default' | 'secondary' | 'outline' | 'destructive' => {
  if (!status) return 'secondary';
  
  switch (status.toLowerCase()) {
    case 'scheduled':
      return 'secondary';
    case 'in_meeting':
    case 'joining':
      return 'default';
    case 'done':
      return 'outline';
    default:
      return 'secondary';
  }
};

const getBotStatusLabel = (t: Dictionary, status: string | null): string => {
  const labels = t.meetings.botStatus;
  if (!status) return labels.scheduled;
  
  switch (status.toLowerCase()) {
    case 'scheduled':
      return labels.scheduled;
    case 'in_meeting':
      return labels.recording;
    case 'joining':
      return labels.joining;
    case 'done':
      return labels.recorded;
    case 'ready':
      return labels.ready;
    default:
      return status;
  }
};

const getPlatformDisplay = (platform: string | null): string => {
  if (!platform) return 'Meeting';
  
  switch (platform.toLowerCase()) {
    case 'zoom':
      return 'Zoom';
    case 'google_meet':
      return 'Google Meet';
    case 'microsoft_teams':
      return 'Teams';
    default:
      return platform;
  }
};

export const MeetingCard: React.FC<MeetingCardProps> = ({ meeting }) => {
  const navigate = useNavigate();
  const { t, locale } = useI18n();
  const isClickable = canViewMeetingDetails(meeting);

  const handleClick = () => {
    if (isClickable) {
      navigate(`/meetings/${meeting.id}`);
    }
  };

  return (
    <Card 
      className={`transition-colors ${isClickable ? 'hover:bg-accent/50 cursor-pointer' : 'opacity-80'}`}
      onClick={handleClick}
    >
      <CardContent className="p-4">
        <div className="space-y-2">
          {/* Title and Platform */}
          <div className="flex items-start justify-between gap-2">
            <h4 className="text-sm font-medium line-clamp-1 flex-1">
              {meeting.title}
            </h4>
            <div className="flex items-center gap-1 flex-shrink-0">
              {meeting.platform && (
                <Video className="h-4 w-4 text-muted-foreground" />
              )}
              {isClickable && (
                <ChevronRight className="h-4 w-4 text-muted-foreground" />
              )}
            </div>
          </div>
          
          {/* Time */}
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <Clock className="h-3 w-3" />
            <span>{formatMeetingTime(meeting.startTime, meeting.endTime, locale)}</span>
          </div>
          
          {/* Platform and Bot Status */}
          <div className="flex items-center gap-2 flex-wrap">
            {meeting.platform && (
              <Badge variant="outline" className="text-xs">
                {getPlatformDisplay(meeting.platform)}
              </Badge>
            )}
            {meeting.botScheduled && meeting.botStatus !== 'kicked' && (
              <Badge variant={getBotStatusVariant(meeting.botStatus)} className="text-xs">
                {getBotStatusLabel(t, meeting.botStatus)}
              </Badge>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

